import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconTypes } from '../../components/foundations/Icon';
import css from './ChatInput.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';

const ChatInput = memo(
    ({
        input,
        handleInputChange,
        handleSubmit,
        isLoading,
        stop,
        onMicClick,
        isMicListening = false,
        micSupported = true,
        containerClassName,
        translate,
    }) => {
        const inputRef = useRef(null);

        // Auto-resize the textarea up to its CSS max-height.
        const autoResize = el => {
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
        };

        useEffect(() => {
            inputRef.current?.focus();
        }, []);

        // Re-run resize when value changes from outside (e.g. mic transcript appends).
        useEffect(() => {
            autoResize(inputRef.current);
        }, [input]);

        const handleChange = e => {
            autoResize(e.target);
            handleInputChange(e);
        };

        const handleKeyDown = e => {
            // Submit on Enter (without Shift)
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
            }
        };

        const handleFormSubmit = e => {
            e.preventDefault();
            if (input.trim() && !isLoading) {
                handleSubmit(e);
            }
        };

        return (
            <div className={classNames(css.chatInputContainer, containerClassName)}>
                <form onSubmit={handleFormSubmit} className={css.chatInputForm}>
                    <div className={css.inputContainer}>
                        <div className={css.inputHeader}>
                            <textarea
                                ref={inputRef}
                                rows={1}
                                className={css.inputField}
                                placeholder={translate(
                                    isLoading
                                        ? `${TRANSLATION_PREFIX}.INPUT_PLACEHOLDER_LOADING`
                                        : `${TRANSLATION_PREFIX}.INPUT_PLACEHOLDER`
                                )}
                                value={input}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                disabled={isLoading}
                            />
                            <div className={css.inputHeaderIcons}>
                                {micSupported && (
                                    <button
                                        type="button"
                                        className={classNames(css.iconButton, {
                                            [css.micListening]: isMicListening,
                                        })}
                                        onClick={onMicClick}
                                        disabled={isLoading}
                                        aria-label={translate(
                                            isMicListening
                                                ? `${TRANSLATION_PREFIX}.MIC_STOP_LISTENING_LABEL`
                                                : `${TRANSLATION_PREFIX}.MIC_LABEL`
                                        )}
                                        title={translate(
                                            isMicListening
                                                ? `${TRANSLATION_PREFIX}.MIC_STOP_LISTENING_LABEL`
                                                : `${TRANSLATION_PREFIX}.MIC_LABEL`
                                        )}
                                        aria-pressed={isMicListening}
                                    >
                                        <Icon
                                            name={isMicListening ? 'mic-fill' : 'mic-line'}
                                            type="mingcute"
                                            disabled={isLoading}
                                            size={IconSizes.SMALL}
                                        />
                                    </button>
                                )}
                                {isLoading ? (
                                    <button
                                        type="button"
                                        className={classNames(css.iconButton, css.sendButton)}
                                        onClick={stop}
                                        aria-label={translate(`${TRANSLATION_PREFIX}.STOP_LABEL`)}
                                        title={translate(`${TRANSLATION_PREFIX}.STOP_LABEL`)}
                                    >
                                        <Icon name="square" type={IconTypes.TABLER} size={IconSizes.SMALL} />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={!input.trim() || isLoading}
                                        className={classNames(css.iconButton, css.sendButton)}
                                        aria-label={translate(`${TRANSLATION_PREFIX}.SEND_LABEL`)}
                                        title={translate(`${TRANSLATION_PREFIX}.SEND_LABEL`)}
                                    >
                                        <Icon
                                            name="arrow-up-fill"
                                            type="mingcute"
                                            size={IconSizes.SMALL}
                                            interactive={false}
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        );
    }
);

ChatInput.propTypes = {
    input: PropTypes.string.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    stop: PropTypes.func.isRequired,
    onMicClick: PropTypes.func,
    isMicListening: PropTypes.bool,
    micSupported: PropTypes.bool,
    containerClassName: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ChatInput);
